import { useQuery } from "@tanstack/react-query";
import { ResearchSheet } from "../types";

interface ApiResearchSheet {
  id: number;
  title: string;
  created_at: string;
  start_date: string;
  attachment: { url: string };
}

interface ApiCategory {
  id: number | string;
  name: string;
  label: string;
  research_sheets: ApiResearchSheet[];
}

interface ApiResponse {
  daily?: ApiCategory[];
  weekly?: ApiCategory[];
  monthly?: ApiCategory[];
}

interface RecentSheetsResult {
  sheets: ResearchSheet[];
  centralBanksSheets: ResearchSheet[];
}

const CENTRAL_BANKS_CATEGORY_ID = 25;

const fetchRecentSheets = async (): Promise<RecentSheetsResult> => {
  const response = await fetch(
    "https://newsquawk.com/research_sheets.json?code=HzoEdUN6bM-UzfaVKkNg"
  );
  const data: ApiResponse = await response.json();

  const sheetsMap: Record<string, ResearchSheet> = {};
  const centralBanksSheets: ResearchSheet[] = [];

  ["daily", "weekly", "monthly"].forEach((period) => {
    (data[period as keyof ApiResponse])?.forEach((category) => {
      const researchSheets = category.research_sheets;

      if (researchSheets && researchSheets.length > 0) {
        researchSheets.sort(
          (a, b) =>
            new Date(b.created_at).getTime() -
            new Date(a.created_at).getTime()
        );
        const latestSheet = researchSheets[0];

        if (!sheetsMap[category.id.toString()]) {
          sheetsMap[category.id.toString()] = {
            id: category.id.toString(),
            title: category.name,
            category: period,
            pdfUrl: "https://newsquawk.com" + latestSheet.attachment.url,
            uploadDate: new Date(
              latestSheet.created_at
            ).toLocaleDateString("en-GB", {
              day: "numeric",
              month: "short",
            }),
            eventDate: new Date(
              latestSheet.start_date
            ).toLocaleDateString("en-GB", {
              day: "numeric",
              month: "short",
            }),
            label: category.label,
          };
        }

        if (
          category.id === CENTRAL_BANKS_CATEGORY_ID ||
          category.id === String(CENTRAL_BANKS_CATEGORY_ID)
        ) {
          researchSheets.forEach((sheet) => {
            centralBanksSheets.push({
              id: `${category.id}-${sheet.id}`,
              title: sheet.title,
              category: period,
              pdfUrl: "https://newsquawk.com" + sheet.attachment.url,
              uploadDate: new Date(sheet.created_at).toLocaleDateString(
                "en-GB",
                {
                  day: "numeric",
                  month: "short",
                }
              ),
              eventDate: new Date(sheet.start_date).toLocaleDateString(
                "en-GB",
                {
                  day: "numeric",
                  month: "short",
                }
              ),
              label: category.label,
            });
          });
        }
      }
    });
  });

  return { sheets: Object.values(sheetsMap), centralBanksSheets };
};

export const useRecentSheets = () => {
  return useQuery({
    queryKey: ["recentSheets"],
    queryFn: fetchRecentSheets,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false,
  });
};
